import { capitalize } from '@mui/material';
import { InvoiceStatus, SubscriptionStatus } from '@prisma/client';
import type Stripe from 'stripe';
import {
  GetSubscriptionResponse,
  SubscriptionModalContent,
} from '../../types/subscriptions';
import { LoadedUser } from '../../interfaces';
import { isSudoManager } from '../../utils/global/helper';

export function getInvoiceStatusText(status: InvoiceStatus) {
  return capitalize(status.toLowerCase());
}

export function getInvoiceStatusDescription(status: InvoiceStatus) {
  switch (status) {
    case InvoiceStatus.DRAFT:
      return 'This invoice is a draft and will collect payment when finalized.';
    case InvoiceStatus.OPEN:
      return 'This invoice is unpaid.';
    case InvoiceStatus.PENDING:
      return 'This invoice is pending and will be marked paid once the payment is processed.';
    case InvoiceStatus.PAID:
      return 'This invoice is paid.';
    case InvoiceStatus.UNCOLLECTIBLE:
    case InvoiceStatus.VOID:
      return 'This invoice is canceled.';
    default:
      return '';
  }
}

export function getSubscriptionModalContentFromStatus(
  status: SubscriptionStatus,
  canManageSubscription = true,
): SubscriptionModalContent | null {
  switch (status) {
    case SubscriptionStatus.PAUSED:
      if (!canManageSubscription) {
        return {
          header: 'Trial Expired',
          title: 'Your free trial has ended',
          description:
            'Your shop’s Porter subscription has ended. You can ask your shop manager to re-activate, or create your own location to keep using Porter.',
          positiveButtonText: 'Create your own location',
          negativeButtonText: 'Close',
          action: 'create_location',
        };
      }
      return {
        header: 'Trial Expired',
        title: 'Your free trial has ended',
        description:
          "To continue using Porter, you'll need to subscribe to a monthly plan. Subscribe to Porter through the link below to regain access to your account!",
        positiveButtonText: 'Subscribe Now',
        negativeButtonText: "I don't want to subscribe",
        action: 'manage',
      };
    case SubscriptionStatus.CANCELED:
      if (!canManageSubscription) {
        return {
          header: 'Subscription Canceled',
          title: 'Your subscription was canceled',
          description:
            'Your shop’s Porter subscription was canceled. You can ask your shop manager to re-activate, or create your own location to keep using Porter.',
          positiveButtonText: 'Create your own location',
          negativeButtonText: 'Close',
          action: 'create_location',
        };
      }
      return {
        header: 'Subscription Canceled',
        title: 'Your subscription was canceled',
        description:
          'To continue using Porter, you’ll need to re-subscribe to our monthly plan.',
        positiveButtonText: 'Subscribe Now',
        negativeButtonText: "I don't want to subscribe",
        action: 'manage',
      };
    case SubscriptionStatus.PAST_DUE:
    case SubscriptionStatus.UNPAID:
    case SubscriptionStatus.INCOMPLETE:
      return {
        header: 'Payment Failed',
        title: 'Payment for subscription failed',
        description: `The payment for your subscription failed. ${
          canManageSubscription
            ? 'Please update your payment information to continue using the Porter app.'
            : 'Please reach out to your shop owner or manager to update your payment information.'
        } If you recently updated your payment information, please allow some time for the changes to take effect.`,
        positiveButtonText: canManageSubscription ? 'Update Payment' : '',
        negativeButtonText: canManageSubscription
          ? "I don't want to subscribe"
          : 'Close',
        action: canManageSubscription ? 'manage' : undefined,
      };
    default:
      return null;
  }
}

export function getSubscriptionModalContent({
  subscriptionData,
  daysRemaining,
  inFreeTrial,
  canManageSubscription,
}: {
  subscriptionData: GetSubscriptionResponse | null | undefined;
  daysRemaining: number;
  inFreeTrial: boolean;
  canManageSubscription: boolean;
}): SubscriptionModalContent | null {
  if (!subscriptionData) return null;

  const { subscription, paymentCustomer, isActive } = subscriptionData;

  if (!subscription || !subscription) return null;

  // no modal when user has valid, renewing subscription
  if (
    isActive &&
    !inFreeTrial &&
    !!paymentCustomer?.paymentMethods?.length &&
    !subscription.isCanceled
  ) {
    return null;
  }

  if (inFreeTrial) {
    const willRenewTrial =
      !!paymentCustomer?.paymentMethods?.length && !subscription?.isCanceled;

    if (willRenewTrial || (daysRemaining !== 7 && daysRemaining !== 3)) {
      return null;
    }
    const header =
      daysRemaining <= 0
        ? 'Last Day'
        : `${daysRemaining} Day${daysRemaining > 1 ? 's' : ''} Left`;
    return {
      header,
      title: 'Your free trial is about to end',
      description: `Once your free trial ends, you'll no longer be able to use the Porter app.${
        canManageSubscription
          ? ' Subscribe through the link below to keep access to your account!'
          : ' Please reach out to your shop owner or manager to keep your account active.'
      }`,
      positiveButtonText: canManageSubscription ? 'Subscribe Now' : '',
      negativeButtonText: canManageSubscription ? 'Not now' : 'Close',
      action: canManageSubscription ? 'manage' : undefined,
    };
  }

  return getSubscriptionModalContentFromStatus(
    subscription.status,
    canManageSubscription,
  );
}

export function canUserManageSubscription(user: LoadedUser | null | undefined) {
  return (
    !!user &&
    (user?.business?.type === 'INDEPENDENT' ||
      (user?.business?.type === 'STUDIO' && isSudoManager(user)))
  );
}

export function convertStripeStatus(
  status: Stripe.Subscription.Status,
): SubscriptionStatus {
  switch (status) {
    case 'active':
      return SubscriptionStatus.ACTIVE;
    case 'canceled':
      return SubscriptionStatus.CANCELED;
    case 'incomplete':
      return SubscriptionStatus.INCOMPLETE;
    case 'incomplete_expired':
      return SubscriptionStatus.INCOMPLETE_EXPIRED;
    case 'past_due':
      return SubscriptionStatus.PAST_DUE;
    case 'trialing':
      return SubscriptionStatus.TRIALING;
    case 'unpaid':
      return SubscriptionStatus.UNPAID;
    case 'paused':
      return SubscriptionStatus.PAUSED;
    default:
      return SubscriptionStatus.ACTIVE;
  }
}

export function convertSubscriptionStatus(
  status: SubscriptionStatus,
): Stripe.Subscription.Status {
  switch (status) {
    case SubscriptionStatus.ACTIVE:
      return 'active';
    case SubscriptionStatus.CANCELED:
      return 'canceled';
    case SubscriptionStatus.INCOMPLETE:
      return 'incomplete';
    case SubscriptionStatus.INCOMPLETE_EXPIRED:
      return 'incomplete_expired';
    case SubscriptionStatus.PAST_DUE:
      return 'past_due';
    case SubscriptionStatus.TRIALING:
      return 'trialing';
    case SubscriptionStatus.UNPAID:
      return 'unpaid';
    case SubscriptionStatus.PAUSED:
      return 'paused';
    default:
      return 'active';
  }
}
